import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import {
  AIMedia,
  AIMediaStatus,
  BrollCaptionVideo,
  Status,
  WebSocketDataReturnType,
} from '../../@types';
import {
  BrollRenderStatus,
  handleDownloadVideoFile,
  MediaStatus,
  STATUS_REGISTRATION_REQUIREMENT,
} from '../../utils';
import { useAppContext, useDialog } from '..';
import { useCloneBrollCaption, useDeleteAiMedia, useUpdateBrollVideoName } from '../../services';
import { useUpdateAiMedia } from '../../../t2c-fe/services/ai-generate/updateAiMedia';

interface Props {
  videoData: AIMedia;
  refreshBrollData?: () => void;
  isImage?: boolean;
}

export const useBrollClipCard = ({ videoData, refreshBrollData = () => {}, isImage }: Props) => {
  const { id, name, url, status, prompt } = videoData;
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [overallPercentage, setOverallPercentage] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useRouter();

  const {
    websocket: { isConnected, sendMessage, aiGenerateTaskMap, handleRemoveFromTaskMap },
  } = useAppContext();

  const aiGenerateTask = aiGenerateTaskMap.get(id) ?? null;
  const {
    status: taskStatus = null,
    progress: taskProgress = 0,
    data: { aiMediaData: { url: updatedUrl = '' } = {}, errorMessage = null } = {},
  } = aiGenerateTask || {};

  const { registrationRequired } = STATUS_REGISTRATION_REQUIREMENT;

  const brollClipStatus = taskStatus || status;
  const brollClipUrl = updatedUrl || url;
  const cardProgress = taskProgress;
  const isInProgress = registrationRequired.includes(brollClipStatus);

  const isTaskSuccessful = taskStatus === Status.Success;
  const isTaskInProgress = taskStatus === Status.InProgress;
  const isTaskFailed = taskStatus === Status.Error;

  useEffect(() => {
    if (isTaskSuccessful || isTaskFailed) {
      refreshBrollData();
    }
  }, [isTaskFailed, isTaskSuccessful]);

  useEffect(() => {
    if (isConnected && registrationRequired.includes(brollClipStatus)) {
      sendMessage({
        event: 'register',
        data: { id },
      });
    }
  }, [isConnected, brollClipStatus, id, sendMessage]);

  useEffect(() => {
    if (taskStatus === Status.Error) {
      refreshBrollData();
    }
  }, [taskStatus]);

  const {
    isDialogOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog();

  const {
    isDialogOpen: isRenameDialogOpen,
    openDialog: openRenameDialog,
    closeDialog: closeRenameDialog,
  } = useDialog();

  const { mutate: deleteVideo, isLoading: isDeletingVideo } = useDeleteAiMedia();

  const { mutate: mutateEditName, isLoading: isEditNameLoading } = useUpdateAiMedia();

  const handleOnCopy = () => {
    navigator.clipboard
      .writeText(prompt || '')
      .then(() => {
        enqueueSnackbar('Text copied to clipboard!', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed to copy text', { variant: 'error' });
      });
  };

  const confirmDeleteVideo = async () => {
    deleteVideo(
      { aiMediaId: id },
      {
        onSuccess: () => {
          refreshBrollData();
          enqueueSnackbar('Video deleted successfully', { variant: 'success' });
          closeDeleteDialog();
        },
        onError: () => {
          enqueueSnackbar('Error deleting video', { variant: 'error' });
        },
      }
    );
  };

  const handleOnClick = () => {
    if (status === AIMediaStatus.Success) {
      push(isImage ? `/text-to-image/${id}` : `/broll/${id}`);
    }
  };

  const initiateDownloadVideo = () => {
    handleDownloadVideoFile({
      url: url,
      name,
      onSuccess: () => setMenuAnchorEl(null),
      format: isImage ? 'image' : 'video',
    });
  };

  const handleOnRename = (name: string) => {
    mutateEditName(
      { mediaId: id, name },
      {
        onSuccess: () => {
          enqueueSnackbar('Name updated successfully!', { variant: 'success' });
          closeRenameDialog();
          refreshBrollData();
        },
        onError: (error: any) => {
          enqueueSnackbar(error?.message || 'Failed to update name. Please try again.', {
            variant: 'error',
          });
          closeRenameDialog();
        },
      }
    );
  };

  const menuActions = [
    {
      id: 'preview',
      label: 'Preview',
      isHidden: status !== AIMediaStatus.Success,
      iconifyIcon: 'mdi:eye-outline',
      onClick: () => {
        push(isImage ? `/text-to-image/${id}` : `/broll/${id}`);
      },
    },
    {
      id: 'rename',
      label: 'Rename',
      iconifyIcon: 'mdi:rename',
      isHidden: status !== AIMediaStatus.Success,
      onClick: () => {
        openRenameDialog();
        setMenuAnchorEl(null);
      },
    },
    {
      id: 'copyPrompt',
      label: 'Copy Prompt',
      iconifyIcon: 'mdi:content-copy',
      isHidden: isInProgress,
      onClick: () => {
        handleOnCopy();
        setMenuAnchorEl(null);
      },
    },
    {
      id: 'editPrompt',
      label: 'Edit Prompt',
      iconifyIcon: 'mdi:clipboard-edit-outline',
      isHidden: isInProgress,
      onClick: () => {
        push(isImage ? `/text-to-image?edit=${id}` : `/broll?edit=${id}`);
        setMenuAnchorEl(null);
      },
    },
    {
      id: 'downloadVideo',
      label: 'Download',
      iconifyIcon: 'material-symbols:download',
      isHidden: !url,
      onClick: initiateDownloadVideo,
    },
    {
      id: 'deleteVideo',
      label: 'Delete',
      iconifyIcon: 'material-symbols:delete',
      isHidden: isInProgress,
      onClick: () => {
        openDeleteDialog();
        setMenuAnchorEl(null);
      },
    },
  ];

  useEffect(() => {
    const { registrationRequired } = STATUS_REGISTRATION_REQUIREMENT;

    if (isConnected && registrationRequired.includes(status)) {
      sendMessage({
        event: 'register',
        data: {
          id,
        },
      });
    }
  }, [isConnected, status, id]);

  return {
    menuAnchorEl,
    setMenuAnchorEl,
    isDeleteDialogOpen,
    closeDeleteDialog,
    confirmDeleteVideo,
    isDeletingVideo,
    menuActions,
    handleOnClick,
    status,
    overallPercentage,
    hideDropdownButton: isInProgress,
    closeRenameDialog,
    isRenameDialogOpen,
    isEditNameLoading,
    handleOnRename,
    brollClipStatus,
    brollClipUrl,
    cardProgress,
  };
};
